/* ── PrivilegeAccountConfiguration ──────────────────────────────────────
   Redesigned PGA Configuration screen:
   - Left panel: Identified Groups / Accounts with search, type filter, selection, and pagination.
   - Right canvas (Top): Privileged Groups container with risk classifications
     (Unassigned, Critical, High, Medium, Low).
   - Right canvas (Bottom): Non-privileged Groups container with drop target.
   - Supports both directory groups and individual accounts with member inspection.
   - Filter bar with Source System, Resources, global search, and Edit / Save controls.
   ──────────────────────────────────────────────────────────────────────── */

// External Libraries
import {
  ArrowRight,
  CheckSquare,
  Filter,
  Info,
  Loader2,
  Lock,
  Pencil,
  RotateCcw,
  Save,
  ShieldCheck,
  ShieldUser,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

// Internal Modules
import { useAuthStore } from "../../../stores/authStore";
import { useSeverityColorSync } from "../../../stores/severityColorStore";
import { useToastStore } from "../../../stores/toastStore";
import { DiscardConfirmDialog, useDiscardGuard } from "../../components/DiscardConfirmDialog";
import { MultiSelectDropdown } from "../../components/MultiSelectDropdown";
import { SearchInput } from "../../components/SearchInput";
import { SkeletonCard } from "../../components/Skeleton";
import { Button } from "../../components/ui/button";
import { cn } from "../../components/ui/utils";
import { G360NavTabs, type G360NavTab } from "../itCompliance/components/G360NavTabs";

// Relative Imports
import { GroupCategoryModal } from "./components/GroupCategoryModal";
import { IdentifiedGroupList } from "./components/IdentifiedGroupList";
import { KanbanGroupCard } from "./components/KanbanGroupCard";
import {
  NON_PRIVILEGED_COLUMN,
  PRIVILEGED_COLUMNS,
  UNIFIED_COLUMN_META,
  UNIFIED_COLUMNS,
  getUnifiedColumnMeta,
  type UnifiedColumn,
  type UnifiedGroup,
} from "./constants/pagAccountConfigConstants";
import {
  getBaselineColumn,
  getCurrentColumn,
  isGroupPrivileged,
  mergeUnifiedGroups,
} from "./helpers/pagAccountConfigHelpers";
import { pagErrorMessage } from "./helpers/pagHelpers";
import { usePagScopeFilter } from "./hooks/pagHooks";
import { IamPolicyCategorization } from "./IamPolicyCategorization";
import {
  fetchPagGroupRisk,
  fetchPagOnPremGroupList,
  savePagGroupPrivilegeRiskConfig,
  type PagGroupPrivilegeRiskConfigRequest,
  type PagGroupRiskAssignmentItem,
} from "./services/pagApi";

export {
  UNIFIED_COLUMNS,
  PRIVILEGED_COLUMNS,
  NON_PRIVILEGED_COLUMN,
  UNIFIED_COLUMN_META,
  buildSavePayload,
  createRiskAssignment,
  type UnifiedColumn,
  type UnifiedGroup,
};

type PgaConfigTab = "risk-classification" | "iam-policy-categorization";

const PGA_CONFIG_TABS: G360NavTab<PgaConfigTab>[] = [
  {
    key: "risk-classification",
    label: "Privilege and Risk Classification",
    icon: SlidersHorizontal,
  },
  {
    key: "iam-policy-categorization",
    label: "IAM Policy Categorization",
    icon: ShieldUser,
  },
];

/* ── Pure Helpers ───────────────────────────────────────────────────── */

/** Filter groups and nested accounts by search term. */
function filterVisibleGroups(groups: UnifiedGroup[], search: string): UnifiedGroup[] {
  const term = search.trim().toLowerCase();
  if (!term) return groups;
  return groups.filter((g) => {
    const matchDirect =
      g.groupName.toLowerCase().includes(term) ||
      (g.userName ?? "").toLowerCase().includes(term) ||
      (g.displayName ?? "").toLowerCase().includes(term) ||
      (g.email ?? "").toLowerCase().includes(term) ||
      (g.description ?? "").toLowerCase().includes(term) ||
      (g.sourceSystemName ?? "").toLowerCase().includes(term);

    if (matchDirect) return true;

    return (
      g.accounts?.some(
        (a) =>
          (a.userName ?? "").toLowerCase().includes(term) ||
          (a.displayName ?? "").toLowerCase().includes(term) ||
          (a.email ?? "").toLowerCase().includes(term),
      ) ?? false
    );
  });
}

function isItemMoved(g: UnifiedGroup, draft: Record<string, UnifiedColumn>): boolean {
  return draft[g.id] !== undefined || getCurrentColumn(g, draft) !== getBaselineColumn(g);
}

function sortGroupsWithMovedFirst(
  items: UnifiedGroup[],
  draft: Record<string, UnifiedColumn>,
): UnifiedGroup[] {
  return [...items].sort((a, b) => {
    const aMoved = isItemMoved(a, draft);
    const bMoved = isItemMoved(b, draft);
    if (aMoved && !bMoved) return -1;
    if (!aMoved && bMoved) return 1;
    return 0;
  });
}

/** Validate and filter item IDs when moving to Identified. */
function filterIdsForMove(
  ids: string[],
  targetColumn: UnifiedColumn,
  groups: UnifiedGroup[],
  draft: Record<string, UnifiedColumn>,
): { allowedIds: string[]; hadNonPrivileged: boolean } {
  if (targetColumn !== "IDENTIFIED") {
    return { allowedIds: ids, hadNonPrivileged: false };
  }

  const nonPrivilegedItems = ids.filter((id) => {
    const g = groups.find((item) => item.id === id);
    return (
      g &&
      (getCurrentColumn(g, draft) === "NON_PRIVILEGED" ||
        getBaselineColumn(g) === "NON_PRIVILEGED")
    );
  });

  if (nonPrivilegedItems.length > 0) {
    return {
      allowedIds: ids.filter((id) => !nonPrivilegedItems.includes(id)),
      hadNonPrivileged: true,
    };
  }

  return { allowedIds: ids, hadNonPrivileged: false };
}

interface DraftPayloadCollector {
  groupNames: string[];
  accountIds: number[];
  assignments: PagGroupRiskAssignmentItem[];
  nonPrivilegedGroupNames: string[];
  nonPrivilegedAccountIds: number[];
}

function recordNonPrivilegedItem(
  item: UnifiedGroup,
  collector: DraftPayloadCollector,
): void {
  if (item.itemType === "GROUP") {
    collector.nonPrivilegedGroupNames.push(item.groupName);
  } else if (typeof item.accountId === "number") {
    collector.nonPrivilegedAccountIds.push(item.accountId);
  }
}

function createRiskAssignment(
  item: UnifiedGroup,
  riskLevel: UnifiedColumn,
  currentUser: string,
): PagGroupRiskAssignmentItem | null {
  if (
    riskLevel === "NON_PRIVILEGED" ||
    riskLevel === "IDENTIFIED" ||
    riskLevel === "AVAILABLE"
  ) {
    return null;
  }
  if (item.itemType === "GROUP") {
    const hasValidGroupId =
      typeof item.groupId === "number" &&
      Number.isFinite(item.groupId) &&
      item.groupId > 0;

    return {
      ...(hasValidGroupId
        ? { groupId: item.groupId }
        : { groupName: item.groupName }),
      riskLevel,
      justification: "Group risk justification",
      assignedBy: currentUser,
    };
  }
  if (typeof item.accountId === "number") {
    return {
      accountId: item.accountId,
      riskLevel,
      justification: "Individual account risk justification",
      assignedBy: currentUser,
    };
  }
  return null;
}

function recordPrivilegedItem(
  item: UnifiedGroup,
  targetCol: UnifiedColumn,
  collector: DraftPayloadCollector,
  currentUser: string,
): void {
  if (item.itemType === "GROUP") {
    collector.groupNames.push(item.groupName);
  } else if (typeof item.accountId === "number") {
    collector.accountIds.push(item.accountId);
  }

  const assignment = createRiskAssignment(item, targetCol, currentUser);
  if (assignment) {
    collector.assignments.push(assignment);
  }
}

function processDraftItem(
  item: UnifiedGroup,
  draft: Record<string, UnifiedColumn>,
  collector: DraftPayloadCollector,
  currentUser: string,
): void {
  const targetCol = getCurrentColumn(item, draft);
  if (targetCol === "NON_PRIVILEGED") {
    recordNonPrivilegedItem(item, collector);
    return;
  }
  if (PRIVILEGED_COLUMNS.includes(targetCol)) {
    recordPrivilegedItem(item, targetCol, collector, currentUser);
  }
}

/** Construct server save payload from partitioned pending draft changes. */
function buildSavePayload(
  sysId: number,
  pendingChanges: UnifiedGroup[],
  draft: Record<string, UnifiedColumn>,
  currentUser: string,
): PagGroupPrivilegeRiskConfigRequest {
  const collector: DraftPayloadCollector = {
    groupNames: [],
    accountIds: [],
    assignments: [],
    nonPrivilegedGroupNames: [],
    nonPrivilegedAccountIds: [],
  };

  for (const item of pendingChanges) {
    processDraftItem(item, draft, collector, currentUser);
  }

  return {
    sourceSystemId: sysId,
    groupNames: Array.from(new Set(collector.groupNames)),
    accountIds: Array.from(new Set(collector.accountIds)),
    assignments: collector.assignments,
    nonPrivilegedGroupNames: Array.from(new Set(collector.nonPrivilegedGroupNames)),
    nonPrivilegedAccountIds: Array.from(new Set(collector.nonPrivilegedAccountIds)),
  };
}

function isDestinationColumnAvailable(
  col: UnifiedColumn,
  selectedIds: string[],
  groups: UnifiedGroup[],
  draft: Record<string, UnifiedColumn>,
): boolean {
  if (col === "AVAILABLE") return false;

  const allAlreadyInColumn = selectedIds.every((id) => {
    const g = groups.find((item) => item.id === id);
    return g && getCurrentColumn(g, draft) === col;
  });
  if (allAlreadyInColumn) return false;

  if (col === "IDENTIFIED") {
    const hasNonPrivilegedSelected = selectedIds.some((id) => {
      const g = groups.find((item) => item.id === id);
      return (
        g &&
        (getCurrentColumn(g, draft) === "NON_PRIVILEGED" ||
          getBaselineColumn(g) === "NON_PRIVILEGED")
      );
    });
    if (hasNonPrivilegedSelected) return false;
  }

  return true;
}

/* ── Subcomponents ──────────────────────────────────────────────────── */

interface PrivilegeConfigFilterToolbarProps {
  scope: ReturnType<typeof usePagScopeFilter>;
  loading: boolean;
  globalSearch: string;
  setGlobalSearch: (val: string) => void;
  onSubmitGlobalSearch?: (val: string) => void;
  totalChangesCount: number;
  editing: boolean;
  saving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
}

function PrivilegeConfigFilterToolbar({
  scope,
  loading,
  globalSearch,
  setGlobalSearch,
  onSubmitGlobalSearch,
  totalChangesCount,
  editing,
  saving,
  onEdit,
  onCancel,
  onSave,
}: PrivilegeConfigFilterToolbarProps) {
  const isFilterDisabled =
    loading || scope.selectedSystems.length === 0 || scope.resources.length === 0;

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4 sm:p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex w-full min-w-[180px] flex-col gap-1.5 sm:w-[200px]">
            <span className="text-[10px] font-semibold tracking-wide text-muted-foreground">
              Source System
            </span>
            <MultiSelectDropdown
              variant="form"
              selectAll
              options={scope.systemOptions}
              selected={scope.selectedSystems}
              onChange={scope.changeSystems}
              placeholder="Select source"
              selectedNoun="Systems"
            />
          </div>
          <div className="flex w-full min-w-[200px] flex-col gap-1.5 sm:w-[240px]">
            <span className="text-[10px] font-semibold tracking-wide text-muted-foreground">
              Resources
            </span>
            <MultiSelectDropdown
              variant="form"
              searchable
              selectAll
              options={scope.resourceOptions}
              selected={scope.resources}
              onChange={scope.setResources}
              placeholder="Select resource"
              selectedNoun="Resources"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={scope.apply}
              disabled={isFilterDisabled}
              variant="indigo"
              className="justify-center min-w-[92px] gap-1.5 px-5 font-semibold disabled:opacity-60"
            >
              <Filter className="size-3.5" />
              Filter
            </Button>
            <Button
              type="button"
              onClick={scope.clear}
              disabled={loading}
              variant="outline"
              className="justify-center min-w-[92px] px-5 hover:border-indigo-400/60 hover:bg-muted/40 disabled:opacity-60"
            >
              Clear
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2.5">
          <SearchInput
            value={globalSearch}
            onChange={setGlobalSearch}
            onSubmit={onSubmitGlobalSearch}
            placeholder="Search groups & accounts..."
            className="w-full sm:w-64"
          />

          {totalChangesCount > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-md bg-indigo-500/10 px-2.5 py-1 text-xs font-semibold text-indigo-300 ring-1 ring-indigo-500/30">
              {totalChangesCount} unsaved {totalChangesCount === 1 ? "change" : "changes"}
            </span>
          )}

          {editing ? (
            <>
              <Button
                type="button"
                onClick={onCancel}
                disabled={saving}
                variant="outline"
                className="justify-start gap-1.5 hover:border-indigo-400/60"
              >
                <RotateCcw className="size-3.5" />
                Cancel
              </Button>
              <Button
                type="button"
                onClick={onSave}
                disabled={saving || totalChangesCount === 0}
                variant="indigo"
                className="justify-start gap-1.5 font-semibold"
              >
                {saving ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Save className="size-3.5" />
                )}
                {saving ? "Saving..." : "Save"}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              onClick={onEdit}
              variant="indigo"
              className="justify-start gap-1.5 font-semibold"
            >
              <Pencil className="size-3.5" />
              Edit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

interface BulkMoveActionBarProps {
  selectedCount: number;
  selectedIds: string[];
  groups: UnifiedGroup[];
  draft: Record<string, UnifiedColumn>;
  severityColors: ReturnType<typeof useSeverityColorSync>;
  onMove: (ids: string[], column: UnifiedColumn) => void;
  onClearSelection: () => void;
}

function BulkMoveActionBar({
  selectedCount,
  selectedIds,
  groups,
  draft,
  severityColors,
  onMove,
  onClearSelection,
}: BulkMoveActionBarProps) {
  const visibleDestinationColumns = UNIFIED_COLUMNS.filter((c) =>
    isDestinationColumnAvailable(c, selectedIds, groups, draft),
  );

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-4 py-3 shadow-sm">
      <span className="text-xs font-medium text-muted-foreground">
        <strong className="text-foreground">{selectedCount}</strong> items selected. Move to:
      </span>
      {visibleDestinationColumns.map((col) => {
        const meta = getUnifiedColumnMeta(col, severityColors);
        return (
          <Button
            key={col}
            variant="ghost"
            onClick={() => {
              onMove(selectedIds, col);
              onClearSelection();
            }}
            className={`justify-start h-auto px-2.5 py-1 rounded-md border text-xs font-medium transition-colors hover:bg-muted/40 ${meta.ring}`}
          >
            <span
              className="mr-1.5 size-1.5 rounded-full"
              style={{ background: meta.color }}
            />
            {meta.label}
          </Button>
        );
      })}
      <Button
        variant="ghost"
        onClick={onClearSelection}
        className="justify-start h-auto px-2 py-1 ml-auto text-xs text-muted-foreground hover:text-foreground"
      >
        Clear selection
      </Button>
    </div>
  );
}

interface PrivilegedColumnsContainerProps {
  privilegedCount: number;
  visibleGroups: UnifiedGroup[];
  draft: Record<string, UnifiedColumn>;
  editing: boolean;
  selectedIds: string[];
  dragOverColumn: UnifiedColumn | null;
  severityColors: ReturnType<typeof useSeverityColorSync>;
  onSelectAllInColumn: (column: UnifiedColumn) => void;
  onToggleSelect: (id: string) => void;
  onDropToColumn: (e: React.DragEvent, column: UnifiedColumn) => void;
  onDragOverColumn: (column: UnifiedColumn) => void;
  onDragLeaveColumn: (column: UnifiedColumn, e: React.DragEvent) => void;
  onViewAll: (column: UnifiedColumn) => void;
}

function PrivilegedColumnsContainer({
  privilegedCount,
  visibleGroups,
  draft,
  editing,
  selectedIds,
  dragOverColumn,
  severityColors,
  onSelectAllInColumn,
  onToggleSelect,
  onDropToColumn,
  onDragOverColumn,
  onDragLeaveColumn,
  onViewAll,
}: PrivilegedColumnsContainerProps) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-red-500/25 bg-red-950/10 p-4 sm:p-5 shadow-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pb-1">
        <div className="flex items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-red-500/30 bg-red-500/20 text-red-500 shadow-sm">
            <Lock className="size-5" />
          </div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm sm:text-base font-bold text-foreground">Privileged</h2>
            <span className="rounded-full border border-red-500/30 bg-red-500/20 px-2.5 py-0.5 text-xs font-semibold text-red-400 tabular-nums">
              {privilegedCount} items
            </span>
          </div>
        </div>

        <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Info className="size-3.5 text-red-400/80" />
          <span>Drag items here to set their risk level</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
        {PRIVILEGED_COLUMNS.map((column) => {
          const meta = getUnifiedColumnMeta(column, severityColors);
          const columnGroups = sortGroupsWithMovedFirst(
            visibleGroups.filter((g) => getCurrentColumn(g, draft) === column),
            draft,
          );
          const isOver = dragOverColumn === column;

          return (
            <div
              key={column}
              onDragOver={(e) => {
                if (!editing) return;
                e.preventDefault();
                onDragOverColumn(column);
              }}
              onDragLeave={(e) => onDragLeaveColumn(column, e)}
              onDrop={(e) => onDropToColumn(e, column)}
              className={cn(
                "flex flex-col rounded-xl border border-border/70 bg-card/70 p-3 transition-colors shadow-sm",
                isOver && "border-indigo-400 bg-indigo-500/15 ring-2 ring-indigo-500/40",
              )}
            >
              <div className="flex items-center justify-between pb-2">
                <div className="flex items-center gap-1.5">
                  <span
                    className="size-2 rounded-full shrink-0"
                    style={{ background: meta.color }}
                  />
                  <h3 className="text-xs font-semibold text-foreground">{meta.label}</h3>
                </div>
                <div className="flex items-center gap-1">
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums"
                    style={{ background: `${meta.color}20`, color: meta.color }}
                  >
                    {columnGroups.length}
                  </span>
                  {editing && columnGroups.length > 0 && (
                    <button
                      type="button"
                      onClick={() => onSelectAllInColumn(column)}
                      title={`Select all in ${meta.label}`}
                      className="rounded p-0.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                    >
                      <CheckSquare className="size-3" />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2 min-h-[140px] flex-1">
                {columnGroups.length === 0 ? (
                  <div className="m-auto text-center text-[10px] text-muted-foreground py-6">
                    {editing ? "Drop items here" : "No items"}
                  </div>
                ) : (
                  columnGroups.slice(0, 3).map((group) => (
                    <KanbanGroupCard
                      key={group.id}
                      group={group}
                      column={column}
                      editing={editing}
                      isSelected={selectedIds.includes(group.id)}
                      isModified={draft[group.id] !== undefined}
                      onToggleSelect={onToggleSelect}
                      selectedIds={selectedIds}
                      variant="compact"
                    />
                  ))
                )}
              </div>

              {columnGroups.length > 0 && (
                <button
                  type="button"
                  onClick={() => onViewAll(column)}
                  className="mt-2 flex items-center justify-center gap-1.5 rounded-lg border border-border/60 bg-muted/30 py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-border hover:bg-muted/70 hover:text-foreground"
                >
                  <span>View all</span>
                  <ArrowRight className="size-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface NonPrivilegedContainerProps {
  nonPrivilegedGroups: UnifiedGroup[];
  editing: boolean;
  selectedIds: string[];
  dragOverColumn: UnifiedColumn | null;
  draft: Record<string, UnifiedColumn>;
  onToggleSelect: (id: string) => void;
  onDropToColumn: (e: React.DragEvent, column: UnifiedColumn) => void;
  onDragOverColumn: (column: UnifiedColumn) => void;
  onDragLeaveColumn: (column: UnifiedColumn, e: React.DragEvent) => void;
  onViewAll: (column: UnifiedColumn) => void;
}

function NonPrivilegedContainer({
  nonPrivilegedGroups,
  editing,
  selectedIds,
  dragOverColumn,
  draft,
  onToggleSelect,
  onDropToColumn,
  onDragOverColumn,
  onDragLeaveColumn,
  onViewAll,
}: NonPrivilegedContainerProps) {
  return (
    <div
      onDragOver={(e) => {
        if (!editing) return;
        e.preventDefault();
        onDragOverColumn(NON_PRIVILEGED_COLUMN);
      }}
      onDragLeave={(e) => onDragLeaveColumn(NON_PRIVILEGED_COLUMN, e)}
      onDrop={(e) => onDropToColumn(e, NON_PRIVILEGED_COLUMN)}
      className={cn(
        "flex flex-col gap-3 rounded-2xl border border-emerald-500/25 bg-emerald-950/10 p-4 sm:p-5 shadow-sm transition-colors",
        dragOverColumn === NON_PRIVILEGED_COLUMN &&
          "border-emerald-400 bg-emerald-500/15 ring-2 ring-emerald-500/40",
      )}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pb-1">
        <div className="flex items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-emerald-500/30 bg-emerald-500/20 text-emerald-400 shadow-sm">
            <ShieldCheck className="size-5" />
          </div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm sm:text-base font-bold text-foreground">Non-privileged</h2>
            <span className="rounded-full border border-emerald-500/30 bg-emerald-500/20 px-2.5 py-0.5 text-xs font-semibold text-emerald-400 tabular-nums">
              {nonPrivilegedGroups.length} items
            </span>
          </div>
        </div>

        <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Info className="size-3.5 text-emerald-400/80" />
          <span>Drag items here to mark them as non-privileged</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 items-center">
        {nonPrivilegedGroups.length === 0 ? (
          <div className="col-span-full py-8 text-center text-xs text-muted-foreground">
            {editing
              ? "Drag groups or accounts here to mark them as non-privileged"
              : "No non-privileged groups or accounts found"}
          </div>
        ) : (
          <>
            {nonPrivilegedGroups.slice(0, 4).map((group) => (
              <KanbanGroupCard
                key={group.id}
                group={group}
                column="NON_PRIVILEGED"
                editing={editing}
                isSelected={selectedIds.includes(group.id)}
                isModified={draft[group.id] !== undefined}
                onToggleSelect={onToggleSelect}
                selectedIds={selectedIds}
              />
            ))}
            {nonPrivilegedGroups.length > 4 && (
              <button
                type="button"
                onClick={() => onViewAll("NON_PRIVILEGED")}
                className="flex h-[52px] items-center justify-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 text-xs font-semibold text-emerald-300 transition-colors hover:bg-emerald-500/20"
              >
                <span>View all ({nonPrivilegedGroups.length})</span>
                <ArrowRight className="size-3.5" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────────────────── */

export const PrivilegeAccountConfiguration = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab =
    (searchParams.get("tab") as PgaConfigTab) || "risk-classification";

  const handleTabChange = (nextTab: PgaConfigTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextTab === "risk-classification") {
        next.delete("tab");
      } else {
        next.set("tab", nextTab);
      }
      return next;
    });
  };

  const severityColors = useSeverityColorSync();
  const scope = usePagScopeFilter();
  const [groups, setGroups] = useState<UnifiedGroup[]>([]);
  const [availableGroupsTotal, setAvailableGroupsTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  /* Pending moves, keyed by item string ID (e.g. "g-243", "a-10095") */
  const [draft, setDraft] = useState<Record<string, UnifiedColumn>>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [globalSearch, setGlobalSearch] = useState("");
  const [appliedGlobalSearch, setAppliedGlobalSearch] = useState("");

  const handleGlobalSearchChange = (val: string) => {
    setGlobalSearch(val);
    if (!val.trim()) {
      setAppliedGlobalSearch("");
    }
  };

  const handleGlobalSearchSubmit = (val: string) => {
    setAppliedGlobalSearch(val.trim());
  };
  const [dragOverColumn, setDragOverColumn] = useState<UnifiedColumn | null>(null);

  /* Modal state for "View all ->" */
  const [viewAllModalColumn, setViewAllModalColumn] = useState<UnifiedColumn | null>(null);

  const { scopeKey, reloadToken, sourceSystemIds } = scope;

  const loadData = useCallback(async () => {
    const ids = scopeKey
      .split(",")
      .map(Number)
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [groupListRes, groupRiskRes] = await Promise.allSettled([
        fetchPagOnPremGroupList(ids),
        fetchPagGroupRisk(ids),
      ]);

      const onPremPayload =
        groupListRes.status === "fulfilled" ? groupListRes.value : undefined;
      const onPremGroups = onPremPayload?.groups ?? [];
      const privilegedGroupList = onPremPayload?.privileged ?? [];
      const riskItems =
        groupRiskRes.status === "fulfilled" ? groupRiskRes.value?.content ?? [] : [];

      const availTotal =
        onPremPayload?.pagination?.availableGroups?.totalElements ??
        (onPremPayload as any)?.availableGroups?.totalElements ??
        (onPremPayload as any)?.pagination?.availableGroups?.totalElements ??
        null;
      setAvailableGroupsTotal(typeof availTotal === "number" ? availTotal : null);

      const result = mergeUnifiedGroups(onPremPayload || onPremGroups, privilegedGroupList, riskItems);
      setGroups(result);
      setDraft({});
      setSelectedIds([]);
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError("Could not load groups", pagErrorMessage(err, "Please retry in a moment."));
      setGroups([]);
      setAvailableGroupsTotal(null);
    } finally {
      setLoading(false);
    }
  }, [scopeKey, reloadToken]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const visibleGroups = useMemo(
    () => filterVisibleGroups(groups, appliedGlobalSearch),
    [groups, appliedGlobalSearch],
  );

  const pendingChanges = useMemo(() => {
    return groups.filter((g) => getCurrentColumn(g, draft) !== getBaselineColumn(g));
  }, [groups, draft]);

  const totalChangesCount = pendingChanges.length;

  const moveTo = (ids: string[], targetColumn: UnifiedColumn) => {
    const { allowedIds, hadNonPrivileged } = filterIdsForMove(
      ids,
      targetColumn,
      groups,
      draft,
    );

    if (hadNonPrivileged) {
      useToastStore
        .getState()
        .showWarning(
          "Action not allowed",
          "Items in Non-privileged cannot be moved to Identified Groups / Accounts.",
        );
      if (!allowedIds.length) return;
    }

    setDraft((prev) => {
      const next = { ...prev };
      allowedIds.forEach((id) => {
        next[id] = targetColumn;
      });
      return next;
    });
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraft({});
    setSelectedIds([]);
  };

  const { requestClose: handleCancelEditing, confirmProps: discardConfirmProps } =
    useDiscardGuard(totalChangesCount > 0, cancelEditing);

  const handleSave = async () => {
    if (totalChangesCount === 0) return;
    setSaving(true);
    try {
      const sysId =
        sourceSystemIds.find((id) => Number.isFinite(id) && id > 0) ??
        groups[0]?.sourceSystemId;

      if (!sysId) {
        useToastStore
          .getState()
          .showError("Save failed", "No valid source system selected.");
        return;
      }

      const currentUser =
        useAuthStore.getState().user?.username ||
        useAuthStore.getState().user?.displayName ||
        "Admin";

      const payload = buildSavePayload(sysId, pendingChanges, draft, currentUser);
      const res = await savePagGroupPrivilegeRiskConfig(payload);

      useToastStore
        .getState()
        .showSuccess(
          "Configuration saved",
          res?.message || "Privilege and risk changes saved successfully.",
        );
      setEditing(false);
      setDraft({});
      setSelectedIds([]);
      await loadData();
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Could not save configuration",
          pagErrorMessage(err, "Please retry in a moment."),
        );
    } finally {
      setSaving(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectAllInColumn = (column: UnifiedColumn) => {
    const columnItemIds = visibleGroups
      .filter((g) => getCurrentColumn(g, draft) === column)
      .map((g) => g.id);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...columnItemIds])));
  };

  const identifiedGroups = useMemo(
    () => visibleGroups.filter((g) => getCurrentColumn(g, draft) === "IDENTIFIED"),
    [visibleGroups, draft],
  );

  const nonPrivilegedGroups = useMemo(
    () =>
      sortGroupsWithMovedFirst(
        visibleGroups.filter((g) => getCurrentColumn(g, draft) === "NON_PRIVILEGED"),
        draft,
      ),
    [visibleGroups, draft],
  );

  const privilegedGroups = useMemo(
    () => visibleGroups.filter((g) => isGroupPrivileged(g, draft)),
    [visibleGroups, draft],
  );

  const unassignedCount = useMemo(
    () =>
      groups.filter((g) => {
        const col = getCurrentColumn(g, draft);
        return col === "IDENTIFIED" || col === "UNASSIGNED";
      }).length,
    [groups, draft],
  );

  const handleDropToColumn = (e: React.DragEvent, targetColumn: UnifiedColumn) => {
    setDragOverColumn(null);
    if (!editing) return;
    e.preventDefault();
    const rawIds = e.dataTransfer.getData("text/plain");
    const ids = rawIds
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!ids.length) return;

    moveTo(ids, targetColumn);
    setSelectedIds([]);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <G360NavTabs
        tabs={PGA_CONFIG_TABS}
        activeTab={
          currentTab === "iam-policy-categorization"
            ? "iam-policy-categorization"
            : "risk-classification"
        }
        onTabChange={handleTabChange}
        ariaLabel="PGA Configuration Tabs"
        size="md"
        className="shrink-0"
      />

      {currentTab === "iam-policy-categorization" ? (
        <IamPolicyCategorization />
      ) : (
        <>
          <div className="shrink-0 text-xs text-muted-foreground">
            {editing
              ? totalChangesCount
                ? `${totalChangesCount} ${totalChangesCount === 1 ? "item" : "items"} moved. Save to apply.`
                : "Drag or select groups/accounts to classify them as privileged risk levels or non-privileged."
              : `${unassignedCount} of ${groups.length} groups and accounts are still uncategorized.`}
          </div>

          <PrivilegeConfigFilterToolbar
            scope={scope}
            loading={loading}
            globalSearch={globalSearch}
            setGlobalSearch={handleGlobalSearchChange}
            onSubmitGlobalSearch={handleGlobalSearchSubmit}
            totalChangesCount={totalChangesCount}
            editing={editing}
            saving={saving}
            onEdit={() => setEditing(true)}
            onCancel={handleCancelEditing}
            onSave={() => void handleSave()}
          />

          {editing && selectedIds.length > 0 && (
            <BulkMoveActionBar
              selectedCount={selectedIds.length}
              selectedIds={selectedIds}
              groups={groups}
              draft={draft}
              severityColors={severityColors}
              onMove={moveTo}
              onClearSelection={() => setSelectedIds([])}
            />
          )}

          {loading ? (
            <div className="flex flex-col gap-4 lg:flex-row">
              <div className="w-full lg:w-[320px] xl:w-[340px]">
                <SkeletonCard lines={8} />
              </div>
              <div className="flex-1 flex flex-col gap-4">
                <SkeletonCard lines={6} />
                <SkeletonCard lines={4} />
              </div>
            </div>
          ) : groups.length === 0 ? (
            <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card p-10 text-center">
              <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-muted/60 text-muted-foreground">
                <Users className="size-6 text-muted-foreground" />
              </div>
              <h4 className="text-base font-semibold text-foreground">No Data Available</h4>
              <p className="mt-1 max-w-sm text-xs text-muted-foreground">
                No directory groups or accounts were discovered or returned for the selected source system.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4 lg:flex-row items-start min-h-0">
              <IdentifiedGroupList
                groups={identifiedGroups}
                totalCount={availableGroupsTotal}
                editing={editing}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onDropToIdentified={(e) => handleDropToColumn(e, "IDENTIFIED")}
                dragOver={dragOverColumn === "IDENTIFIED"}
                onDragOver={(e) => {
                  if (!editing) return;
                  e.preventDefault();
                  if (dragOverColumn !== "IDENTIFIED") setDragOverColumn("IDENTIFIED");
                }}
                onDragLeave={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                  setDragOverColumn((prev) => (prev === "IDENTIFIED" ? null : prev));
                }}
              />

              <div className="flex flex-1 flex-col gap-4 min-w-0 w-full">
                <PrivilegedColumnsContainer
                  privilegedCount={privilegedGroups.length}
                  visibleGroups={visibleGroups}
                  draft={draft}
                  editing={editing}
                  selectedIds={selectedIds}
                  dragOverColumn={dragOverColumn}
                  severityColors={severityColors}
                  onSelectAllInColumn={selectAllInColumn}
                  onToggleSelect={toggleSelect}
                  onDropToColumn={handleDropToColumn}
                  onDragOverColumn={(col) => {
                    if (dragOverColumn !== col) setDragOverColumn(col);
                  }}
                  onDragLeaveColumn={(col, e) => {
                    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                    setDragOverColumn((prev) => (prev === col ? null : prev));
                  }}
                  onViewAll={setViewAllModalColumn}
                />

                <NonPrivilegedContainer
                  nonPrivilegedGroups={nonPrivilegedGroups}
                  editing={editing}
                  selectedIds={selectedIds}
                  dragOverColumn={dragOverColumn}
                  draft={draft}
                  onToggleSelect={toggleSelect}
                  onDropToColumn={handleDropToColumn}
                  onDragOverColumn={(col) => {
                    if (dragOverColumn !== col) setDragOverColumn(col);
                  }}
                  onDragLeaveColumn={(col, e) => {
                    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                    setDragOverColumn((prev) => (prev === col ? null : prev));
                  }}
                  onViewAll={setViewAllModalColumn}
                />
              </div>
            </div>
          )}

          <GroupCategoryModal
            open={viewAllModalColumn !== null}
            onOpenChange={(open) => {
              if (!open) setViewAllModalColumn(null);
            }}
            column={viewAllModalColumn}
            groups={
              viewAllModalColumn
                ? sortGroupsWithMovedFirst(
                    visibleGroups.filter((g) => getCurrentColumn(g, draft) === viewAllModalColumn),
                    draft,
                  )
                : []
            }
            editing={editing}
            onMoveGroup={moveTo}
          />
        </>
      )}

      <DiscardConfirmDialog {...discardConfirmProps} />
    </div>
  );
};

export default PrivilegeAccountConfiguration;

